import { useEffect, useState } from 'react';
import _ from 'lodash';

import { RadioButtonGroup } from '@components/Inputs';
import StatusInput from '../../Status/StatusInput';
import act from '@flux/actions/actView';
import { Button, Grid, Modal, Typography, Checkbox } from '@ts/storybook';
import { CElementType, CStatusIndicator, IForm } from '@ts/ui-models';
import { NewPaperWithPadding } from '@components/Misc';

interface ITemplateRulesFormProps extends IForm {
  values: CElementType;
}

const TemplateRulesForm = ({ mode, values, onCallbackChange, onChange }: ITemplateRulesFormProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [debouncingPropagation, setDebouncingPropagation] = useState(false);
  const [showNameConfig, setShowNameConfig] = useState(!!values.setItemName);

  const fDisabled = mode === 'view';

  useEffect(() => {
    if (values.templateStatusMode === 'none') {
      onCallbackChange('templateStatusMode')('strict');
    }
  }, [values.templateStatusMode, onCallbackChange]);

  const handleUpdateItems = () => {
    const requestData = {
      jobType: 'itemTypeStatusTemplatePropagation',
      jobData: { itemTypeLogicalId: values.logicalId },
    };
    act.push({ url: '/api/queuedJob', data: requestData });
    act.notify({ msg: 'Items status update request sent' });
    setDebouncingPropagation(true);
    setTimeout(() => setDebouncingPropagation(false), 5000);
  };

  const onShowNaming = (value: boolean) => {
    if (value) {
      onCallbackChange('setItemName')(null);
    }
    setShowNameConfig(value);
  };

  const onChangeTypeParams = (key: string) => (value: any) => {
    const typeParams = structuredClone(values.typeParams) || {};
    typeParams[key] = value;
    onChange('typeParams', typeParams);
  };

  const renderStatusInput = () => {
    if (values.templateStatusMode === 'none') return null;
    return (
      <div style={{ marginTop: '0.5rem' }}>
        <StatusInput
          mode={mode}
          title={'Indicators to inherit'}
          statesList={values.templateStatus.indicators.map(i => new CStatusIndicator(i))}
          onChangeList={newList => {
            let newStatus = structuredClone(values.currentStatus);
            if (newStatus) newStatus.indicators = newList;
            return onCallbackChange('templateStatus')(newStatus);
          }}
          showUpdate
        />
        <Typography
          variant="body1"
          textAlign="center"
          text={
            values.templateStatusMode === 'strict' ? 'Warning, removing indicators in strict mode will delete them on items.' : ''
          }
        />
        <div className="text-center">
          <Button
            variant="outlined"
            onClick={_.debounce(handleUpdateItems, 5000, {
              leading: true,
              trailing: false,
            })}
            disabled={mode !== 'view' || debouncingPropagation}
            label="Update items now"
          />
        </div>
        <RadioButtonGroup
          key="replaceInvalidValues"
          title={['Set invalid values to', ':']}
          value={_.get(values.typeParams, 'replaceInvalidValues', 'template')}
          onChange={onChangeTypeParams('replaceInvalidValues')}
          options={[
            { value: 'template', label: 'Template default value', disabled: fDisabled },
            { value: 'remove', label: 'Remove the current value', disabled: fDisabled },
          ]}
        />
        <Checkbox
          disabled={fDisabled}
          label="Update items upon save"
          checked={values.propagateTemplate || false}
          onChangeValue={onCallbackChange('propagateTemplate')}
        />
      </div>
    );
  };

  const renderNameConfiguration = () => (
    <>
      <Checkbox disabled={fDisabled} label="Set name for new items" checked={showNameConfig} onChangeValue={onShowNaming} />
      {showNameConfig && (
        <RadioButtonGroup
          key="nameOfNewItem"
          value={values.setItemName}
          onChange={onCallbackChange('setItemName')}
          options={[
            { value: 'itemName', label: 'Item name', disabled: fDisabled },
            { value: 'uniqueID', label: 'Unique item ID', disabled: fDisabled },
            { value: 'name+uniqueID', label: 'Name + unique ID', disabled: fDisabled },
          ]}
        />
      )}
    </>
  );

  const renderModalContent = () => (
    <Grid container spacing={2} p={4}>
      <Grid size={12}>
        <Typography variant="h3" text="Warning" />
      </Grid>
      <Grid size={12}>
        <Typography variant="h6" text='Changing template mode from "indicative" to "strict" is a destructive operation.' />
      </Grid>
      <Grid size={12}>
        <Typography variant="h6" text="All items of this type will lose the indicators that are not specified in the template." />
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Typography variant="h5" mt={2} text={[<b>Rules to apply at new items</b>]} />
      <NewPaperWithPadding>
        <Grid container size={12} direction={'column'} sx={{ justifyContent: 'flex-start' }} spacing={2}>
          {renderStatusInput()}
          <Modal
            withHeader
            title=""
            labelOk="Ok, change it"
            onSubmit={() => setShowWarning(false)}
            onClose={() => {
              onCallbackChange('templateStatusMode')('free');
              setShowWarning(false);
            }}
            open={showWarning}
          >
            {renderModalContent()}
          </Modal>
        </Grid>
      </NewPaperWithPadding>
      <NewPaperWithPadding>
        <Typography variant="h6" text="Name configuration" />
        {renderNameConfiguration()}
      </NewPaperWithPadding>
    </Grid>
  );
};

export default TemplateRulesForm;
